import { useEffect, useState } from "react";
import { ArrowLeft, ShieldAlert, Loader2, RefreshCw, Inbox } from "lucide-react";
import { vaultSecurityApi } from "@/api/vaultSecurity";

function fmtTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

/* Founder-only, read-only security activity feed sourced from the silent
   vault_access_log audit trail. Neo-Brutalist, S20-chassis safe. */
export default function VaultActivityLog({ onClose }) {
  const [state, setState] = useState("loading"); // loading | error | ready
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);

  const load = () => {
    setState("loading");
    vaultSecurityApi.activityLog(50, 0)
      .then((d) => { setEntries(d.entries || []); setTotal(d.total || 0); setState("ready"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="absolute inset-0 z-[60] flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-activity-log">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-2 border-gold/20">
        <button onClick={onClose} data-testid="activity-log-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} className="text-gold" />
        </button>
        <div className="flex-1 min-w-0 leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">Secure Vault · Audit</p>
          <h1 className="font-serif text-2xl text-white">Access Activity</h1>
        </div>
        <button onClick={load} data-testid="activity-log-refresh" aria-label="Refresh" className="w-10 h-10 rounded-full bg-white/10 border-2 border-white/25 grid place-items-center active:scale-95">
          <RefreshCw size={16} className="text-white/70" />
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-2.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40" data-testid="activity-log-count">
          {state === "ready" ? `${total} recent access attempt${total === 1 ? "" : "s"}` : "Vault access attempts"}
        </p>

        {state === "loading" && (
          <div className="grid place-items-center py-16" data-testid="activity-log-loading">
            <Loader2 size={22} className="animate-spin text-gold" />
          </div>
        )}

        {state === "error" && (
          <div className="border-2 border-red-500 rounded-2xl bg-red-500/10 p-5 text-center shadow-[4px_4px_0px_#000]" data-testid="activity-log-error">
            <ShieldAlert size={26} className="text-red-400 mx-auto" />
            <p className="mt-2 font-mono text-xs text-white/70">Could not load the activity log.</p>
            <button onClick={load} className="mt-3 font-mono text-[11px] font-black uppercase tracking-widest text-black bg-gold px-4 py-2 rounded-full border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-[1px]">Retry</button>
          </div>
        )}

        {state === "ready" && entries.length === 0 && (
          <div className="border-2 border-black rounded-2xl bg-white/[0.06] p-8 text-center shadow-[4px_4px_0px_#000]" data-testid="activity-log-empty">
            <Inbox size={28} className="text-white/40 mx-auto" />
            <p className="mt-3 font-mono text-xs text-white/60">No recent vault access attempts.</p>
          </div>
        )}

        {state === "ready" && entries.map((e, i) => (
          <div key={`${e.ts}-${i}`} data-testid={`activity-row-${i}`} className="border-2 border-black rounded-xl bg-white/[0.06] p-3 shadow-[3px_3px_0px_#000]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-black text-white truncate" data-testid={`activity-actor-${i}`}>{e.actor}</span>
              <span className="shrink-0 font-mono text-[9px] font-black uppercase tracking-widest text-white bg-red-500 border-2 border-black rounded-full px-2 py-0.5" data-testid={`activity-outcome-${i}`}>{e.outcome}</span>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-white/60 truncate">{e.area}</span>
              <span className="shrink-0 font-mono text-[10px] text-white/40 tabular-nums">{fmtTs(e.ts)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
